import React, { useCallback } from 'react';
import { useLazyQuery } from 'apollo-client-ppc';
import { convertArrayToCSV } from 'convert-array-to-csv';
import { format } from 'date-fns';
import FileSaver from 'file-saver';
import capitalize from 'lodash/capitalize';
import moment from 'moment';
import { Appearance } from '@atlaskit/button';
import { CommerceAddress } from '@atlassian/cssxp-types-opportunity';
import { NEW_DATE_FORMAT } from '@atlassian/cssxp-typescommon';
import { generatedTypesNew } from '@css-xp/graphtypes';
import { getProductUsageDetails } from '@css-xp/base/common-tabs';
import { getNextBillDateText } from '@css-xp/ppc-common';
import { formatAddressLabel, useFeatureToggle } from '@css-xp/base';
import { STATSIG_FEATURE_GATES } from '@css-xp/ppc-constants';
import { useLoadEntitlementList } from '@css-xp/ppc-custom-hooks';
import { fetchEntitlementListForDownloadCSV } from '../queries/fetch-entitlement-list-for-download-csv';
import { fetchTransactionAccountForDownloadCSV } from '../queries/fetch-transaction-account-for-download-csv';
import {
  defaultSortOptions,
  DownloadCSVButton,
  DownloadCSVCommonProps,
  DownloadCSVUsingGetAggregateCcpList,
  headers,
  useVariables,
} from './download-csv-common';

const getCustomerContactsDetails = (endCustomerContacts: Array<{ name: string }> | null | undefined) => {
  const eccData: Array<string> = [];
  endCustomerContacts?.forEach((customerContact) => {
    eccData.push(customerContact.name);
  });
  return eccData.join(', ');
};

export const getCsvHeaders = (indexOfColumnsToBeRemoved: Array<number>) => {
  if (indexOfColumnsToBeRemoved.length > 0) {
    return headers.filter((_header, index) => !indexOfColumnsToBeRemoved.includes(index));
  }
  return headers;
};

type ProductType = NonNullable<
  NonNullable<
    generatedTypesNew.FetchEntitlementListForDownloadCsvQuery['transactionAccount']['productSubscriptionsInPPC']
  >['nodes']
>[number];

export const getTransformedProductDetails = (
  product: ProductType,
  transactionAccount: generatedTypesNew.FetchTransactionAccountForDownloadCsvQuery['transactionAccount'] | undefined,
  indexOfColumnsToBeRemoved: Array<number>
): string[] => {
  const invoiceGroup = transactionAccount?.availableInvoiceGroups?.nodes?.find(
    (invoiceGroup) => invoiceGroup?.id === product?.billingGroupId
  );

  const csvDetails = [
    // col 1: Subscription Id //@ts-ignore
    product?.id ?? '-',
    // col 2: Subscription base product name
    product?.baseProduct ?? '-',
    // col 3: edition : Premium, Standard, Free
    product?.edition ?? '-',
    // col 4: entitlement Number (Slug) info:
    product?.entitlementNumber ?? '-',
    // col 5: Organization name
    product?.orgName ?? '-',
    // col 6: cloud site name
    product?.cloudSiteHostname ?? '-',
    // col 7: product usage details in users / tiers / units
    getProductUsageDetails(product as generatedTypesNew.InvoiceGroupProduct | null),
    // col 8: Next bill / Expiry date
    getNextBillDateText(product as generatedTypesNew.TransactionAccountProduct | null),
    // col 9: Billing cycle
    product?.billingFrequency ? capitalize(product.billingFrequency) : '-',
    // col 10: billing Status: Active / Deactivated
    product?.billingStatus ? capitalize(product?.billingStatus) : '-',
    // col 11: linked apps
    product?.linkedAppsId?.length.toString() ?? '-',
    // col 12: customer contacts
    getCustomerContactsDetails(product?.endCustomerContacts),
    // col 13: Company
    invoiceGroup?.shipToParty?.name ?? '-',
    // col 14: Sold-to address
    formatAddressLabel(invoiceGroup?.shipToParty as CommerceAddress) || '-',
    // col 15: Created
    moment.utc(product?.createdDate).format(NEW_DATE_FORMAT),
  ];

  if (indexOfColumnsToBeRemoved.length > 0) {
    return csvDetails.filter((_csvDetail, index) => !indexOfColumnsToBeRemoved.includes(index));
  }

  return csvDetails;
};

interface props {
  transactionAccountId: string;
  buttonAppearance?: Appearance;
  buttonText?: string;
  className?: string;
  filterOptions?: generatedTypesNew.FilterOption[];
  isDarkTheme?: boolean;
  sortOptions?: generatedTypesNew.Maybe<generatedTypesNew.SortType>;
  relatedDomains?: string[];
  shouldFetchInternalLicenses?: boolean;
}

const DownloadCSVUsingLoadEntitlementList = ({
  variables,
  transactionAccountDataLoading,
  ...props
}: DownloadCSVCommonProps) => {
  const { isLoading: isEntitlementListLoading, loadEntitlementList } = useLoadEntitlementList(
    fetchEntitlementListForDownloadCSV,
    variables
  );
  const buttonLoading = transactionAccountDataLoading || isEntitlementListLoading;
  return (
    <DownloadCSVButton
      {...{
        buttonLoading,
        loadEntitlementList,
        ...props,
      }}
    />
  );
};

export const DownloadCSVPerfV2 = ({
  transactionAccountId,
  buttonAppearance,
  buttonText,
  className,
  filterOptions = [],
  isDarkTheme = false,
  sortOptions = defaultSortOptions,
  relatedDomains,
  shouldFetchInternalLicenses,
}: props) => {
  const [isEnabled] = useFeatureToggle();
  const isUseGetAggregateCcpListHookEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_USE_GET_AGGREGATE_CCP_LIST_HOOK);
  const isSandboxSupportForSubscriptionsEnabled = isEnabled(
    STATSIG_FEATURE_GATES.ENABLE_SANDBOX_SUPPORT_FOR_SUBSCRIPTIONS
  );

  const variables = useVariables({
    transactionAccountId,
    filterOptions,
    sortOptions,
    isSandboxSupportForSubscriptionsEnabled,
  });
  const [loadTransactionAccountData, { loading: transactionAccountDataLoading }] = useLazyQuery<
    generatedTypesNew.FetchTransactionAccountForDownloadCsvQuery,
    generatedTypesNew.FetchTransactionAccountForDownloadCsvQueryVariables
  >(fetchTransactionAccountForDownloadCSV, {
    variables: { id: transactionAccountId },
  });

  const handleDownloadClick = useCallback(
    async (loadEntitlementList) => {
      const [entitlementList, { data: transactionAccountData }] = await Promise.all([
        loadEntitlementList(),
        loadTransactionAccountData(),
      ]);

      const indexOfColumnsToBeRemoved: Array<number> = [];
      indexOfColumnsToBeRemoved.push(headers.indexOf('Customer contacts'));

      indexOfColumnsToBeRemoved.sort();

      const formattedEntitlementData: string[][] = [getCsvHeaders(indexOfColumnsToBeRemoved)];

      if (entitlementList && entitlementList?.length > 0) {
        entitlementList.forEach((entitlement: ProductType) => {
          formattedEntitlementData.push(
            getTransformedProductDetails(
              entitlement,
              transactionAccountData?.transactionAccount,
              indexOfColumnsToBeRemoved
            )
          );
        });
      }
      const csv = convertArrayToCSV(formattedEntitlementData);
      const csvFile = new Blob([csv], { type: 'text/csv' });
      const fileName = `subscriptions-${format(new Date(), 'yyyy-MM-dd-HH-mm-ss')}.csv`;
      FileSaver.saveAs(csvFile, fileName);
    },
    [loadTransactionAccountData]
  );

  const commonProps: DownloadCSVCommonProps = {
    transactionAccountId,
    variables,
    buttonAppearance,
    buttonText,
    className,
    isDarkTheme,
    transactionAccountDataLoading,
    handleDownloadClick,
    shouldFetchInternalLicenses,
    relatedDomains,
  };

  return isUseGetAggregateCcpListHookEnabled ? (
    <DownloadCSVUsingGetAggregateCcpList {...commonProps} />
  ) : (
    <DownloadCSVUsingLoadEntitlementList {...commonProps} />
  );
};
